// =====================================================================
// TempsPresence.jsx - Pagina unificata Temps & Presence (Day 4)
// =====================================================================
// Layout Option C: 1 link in sidebar (/temps), 5 vue-uri interne.
//
// Tabs:
//   1. Mes heures            - MesHeures embedded (user self-service)
//   2. Toutes les feuilles   - AdminGlobalTimesheets embedded (admin grid)
//   3. Validation TS         - AdminValidationTSPage embedded (admin approvals)
//   4. Presence              - Presence embedded (admin matrix)
//   5. Heures sup.           - AdminOvertime embedded (admin overtime)
//
// Default tab:
//   - User pur (timesheets.read.own) -> 'mes-heures'
//   - Admin (read.all + approve)     -> 'validation' daca exista pendings
//                                       altfel 'toutes'
// =====================================================================

import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.jsx';
import Breadcrumbs from '../components/Breadcrumbs.jsx';
import MesHeures from './MesHeures.jsx';
import AdminGlobalTimesheets from './admin/AdminGlobalTimesheets.jsx';
import AdminValidationTSPage from './admin/ValidationTS.jsx';
import Presence from './admin/Presence.jsx';
import AdminOvertime from './admin/Overtime.jsx';

const tabBtn = (active) => ({
  background: 'transparent',
  border: 0,
  padding: '0.75rem 1.25rem',
  fontSize: '0.875rem',
  fontWeight: active ? 600 : 400,
  color: active ? '#2563eb' : '#6b7280',
  borderBottom: active ? '2px solid #2563eb' : '2px solid transparent',
  cursor: 'pointer',
  marginBottom: -1,
});

export default function TempsPresence() {
  const { can } = useAuth();

  const canReadOwn    = can('timesheets.read.own');
  const canReadAll    = can('timesheets.read.all');
  const canApprove    = can('timesheets.approve.group');
  const canPresence   = can('presence.read.all');
  const canOvertime   = can(['overtime.read.all', 'overtime.read.own', 'overtime.create.all']);

  // Default tab: smart based on role
  const defaultTab = canApprove ? 'validation'
                    : canReadAll ? 'toutes'
                    : canReadOwn ? 'mes-heures'
                    : (canPresence ? 'presence' : (canOvertime ? 'overtime' : 'mes-heures'));

  // Day 9: support URL hash to force specific tab (e.g. /temps#mes-heures or /temps#overtime:soldes-operateurs)
  const location = useLocation();
  const VALID_TABS = ['mes-heures', 'toutes', 'validation', 'presence', 'overtime'];
  // Strip everything after ':' so nested sub-tabs (e.g. overtime:soldes-operateurs) still match the top tab
  const hashTab = location.hash.replace('#', '').split(':')[0];
  const initialTab = VALID_TABS.includes(hashTab) ? hashTab : defaultTab;

  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    if (VALID_TABS.includes(hashTab)) setActiveTab(hashTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  // Breadcrumbs tab labels
  const TAB_LABELS = {
    'mes-heures': 'Mes heures',
    'toutes':     'Toutes les feuilles',
    'validation': 'Validation TS',
    'presence':   'Présence',
    'overtime':   'Heures sup.',
  };

  // Access gate
  if (!canReadOwn && !canReadAll && !canApprove && !canPresence && !canOvertime) {
    return (
      <div style={{ padding: 40 }}>
        <h1>Temps &amp; Présence</h1>
        <p style={{ color: '#6b7280', marginTop: 16 }}>Permission refusée.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem 2rem' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>Temps &amp; Présence</h1>
        <span className="hide-mobile" style={{ fontSize: '0.75rem', color: '#9ca3af', fontFamily: 'monospace' }}>feuilles de temps · présence · heures sup.</span>
      </div>
      <p className="hide-mobile" style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0 0 1.25rem' }}>
        Saisie et validation des temps, présences et heures supplémentaires.
      </p>

      <div className="hide-mobile">
        <Breadcrumbs items={[
          { label: 'Temps & Présence', onClick: () => setActiveTab(defaultTab) },
          { label: TAB_LABELS[activeTab] || activeTab },
        ]} />
      </div>

      <div className="mobile-tabs-scroll" style={{ borderBottom: '1px solid #e5e7eb', marginBottom: '1.5rem' }}>
        {canReadOwn && (
          <button style={tabBtn(activeTab === 'mes-heures')} onClick={() => setActiveTab('mes-heures')}>
            🕐 Mes heures
          </button>
        )}
        {canReadAll && (
          <button style={tabBtn(activeTab === 'toutes')} onClick={() => setActiveTab('toutes')}>
            📊 Toutes les feuilles
          </button>
        )}
        {canApprove && (
          <button style={tabBtn(activeTab === 'validation')} onClick={() => setActiveTab('validation')}>
            ✓ Validation TS
          </button>
        )}
        {canPresence && (
          <button style={tabBtn(activeTab === 'presence')} onClick={() => setActiveTab('presence')}>
            📅 Présence
          </button>
        )}
        {canOvertime && (
          <button style={tabBtn(activeTab === 'overtime')} onClick={() => setActiveTab('overtime')}>
            ⏰ Heures sup.
          </button>
        )}
      </div>

      {activeTab === 'mes-heures' && canReadOwn  && <MesHeures embedded />}
      {activeTab === 'toutes'     && canReadAll  && <AdminGlobalTimesheets embedded />}
      {activeTab === 'validation' && canApprove  && <AdminValidationTSPage embedded />}
      {activeTab === 'presence'   && canPresence && <Presence embedded />}
      {activeTab === 'overtime'   && canOvertime && <AdminOvertime embedded />}
    </div>
  );
}
